import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  CheckboxGroupInput,
  CustomDatePicker,
  CustomInput,
  CustomInternationalPhoneInput,
  CustomSelect,
  CustomTextArea,
  dayjsWithTimezone,
  disabledDateRule,
  parseHtml,
  twClassMerge,
  validateIsNumber,
  validatePostalCode,
  validateWhiteSpace,
  validDateRule,
} from '@healthhub/react-ui';
import { Form, InputNumber } from 'antd';
import type { Rule } from 'antd/es/form';
import DOMPurify from 'dompurify';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { OutlinedAddIcon } from '@shared/assets-icon';
import { CustomRadioOption } from '@shared/components';
import { showErrorModal } from '@shared/components/Modals';
import { useLayoutViewContext } from '@shared/context';
import useToggle from '@shared/hooks/useToggle/useToggle';
import { QuestionType } from '@shared/services/appointment/form/types/appointmentForm.type';
import type { QuestionnaireItem } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { ValidationsKeys } from '@shared/services/appointment/questionnaire/types/getQuestionnaire.type';
import { useLazyGetPostalAddressQuery } from '@shared/services/location';
import { useAppDispatch, useAppSelector } from '@shared/stores/rootTypes';

import { TODAY, YES_NO_QUESTIONS } from '@features/Appointment/constants';
import { KKH_QUESTIONNAIRE_IDS } from '@features/Appointment/Questionnaire/constants';
import { currentNumberOfAddButtonSelector } from '@features/Appointment/stores/selector';
import { appointmentAction } from '@features/Appointment/stores/slice';
import { getQuestionnaireRule, requiredRule } from '@features/Appointment/utils/form-rules';
import { getQuestionnaireExceptionType } from '@features/Appointment/utils/questionnaire';
import { checkHtmlContainsString, removeHtmlInString } from '@features/Appointment/utils/tools';

import HelperQuestionnaireModal from './HelperQuestionnaireModal';
import QuestionForm from './QuestionForm';
import Button from '../Button/Button';
import QuestionnaireGroup from './QuestionnaireGroup/QuestionnaireGroup';

const { useWatch } = Form;

interface QuestionnaireProps {
  questionItem: QuestionnaireItem;
}

const Questionnaire = ({ questionItem }: QuestionnaireProps) => {
  const { isMobileView, isSharedServiceView } = useLayoutViewContext();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const form = Form.useFormInstance();
  const formValue = useWatch([], form);
  const [searchParams] = useSearchParams();
  const questionnaireId = searchParams.get('id');
  const [isVisible, onToggle] = useToggle();
  const isFirstRender = useRef(true);

  const currentNumberOfAddButton = useAppSelector(currentNumberOfAddButtonSelector);

  const [fetchPostalAddress] = useLazyGetPostalAddressQuery();

  const {
    AdditionalItem,
    AdditionalQuestionKey,
    AnswerOption,
    CurrentNumberOfAddButton,
    GroupItem,
    HelperItem,
    IsDropDown,
    IsHorizontal,
    IsRootGroup,
    LinkId,
    MaxNumberOfAddButton,
    Repeats,
    Required,
    Text,
    Type,
    Validations,
  } = questionItem;

  const storeCurrentAddButtonCount = useCallback(
    (question?: QuestionnaireItem) => {
      if (!question) return;

      if (question?.Repeats && question?.Type === QuestionType.Group) {
        dispatch(
          appointmentAction.setPreQuestionnaire({
            currentNumberOfAddButton: {
              ...currentNumberOfAddButton,
              [question?.LinkId]: currentNumberOfAddButton?.[question?.LinkId] ?? question?.CurrentNumberOfAddButton,
            },
          })
        );
      }
    },
    [currentNumberOfAddButton, dispatch]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      storeCurrentAddButtonCount(questionItem);
    }

    return () => {
      isFirstRender.current = false;
    };
  }, [questionItem, storeCurrentAddButtonCount]);

  const questionType = getQuestionnaireExceptionType({ IsDropDown, Repeats, type: Type });

  const validationRules = Validations?.map((validation) => getQuestionnaireRule(validation));

  const isPhoneNumberField = Validations?.some(
    (validation) =>
      validation.Key === ValidationsKeys.PhoneNumber || validation.Key === ValidationsKeys.AdditionalPhoneNumber
  );

  const isEmailField = Validations?.some((validation) => validation.Key === ValidationsKeys.Email);

  const isUnitNumberField = Text?.toLowerCase().includes('unit') && LinkId === 'Q004';

  const isAddressField = Text?.toLowerCase().includes('address') && LinkId === 'Q005';

  const isNameField = Text?.toLowerCase().includes('name') && (LinkId === 'Q000' || LinkId === 'KKH000');

  const getAutoComplete = useCallback(() => {
    if (isEmailField) {
      return 'email';
    }
    if (isUnitNumberField) {
      return 'address-line2';
    }

    if (isAddressField) {
      return 'address-line3';
    }

    if (isNameField) {
      return 'name';
    }

    return 'off_autocomplete';
  }, [isEmailField, isUnitNumberField, isAddressField, isNameField]);

  const isValidQuestionType = useMemo(
    () =>
      [
        QuestionType.Boolean,
        QuestionType.CheckBox,
        QuestionType.Choice,
        QuestionType.Date,
        QuestionType.Decimal,
        QuestionType.Dropdown,
        QuestionType.Integer,
        QuestionType.PostalCode,
        QuestionType.String,
        QuestionType.Text,
      ].includes(
        questionType as
          | typeof QuestionType.Boolean
          | typeof QuestionType.CheckBox
          | typeof QuestionType.Choice
          | typeof QuestionType.Date
          | typeof QuestionType.Decimal
          | typeof QuestionType.Dropdown
          | typeof QuestionType.Integer
          | typeof QuestionType.PostalCode
          | typeof QuestionType.String
          | typeof QuestionType.Text
      ),
    [questionType]
  );

  const onGetMyAddress = useCallback(
    async (code: string) => {
      const { data: postalCode, error } = await fetchPostalAddress({ postalCode: code });
      if (postalCode) {
        form.setFieldsValue({
          Q005: postalCode?.address,
        });
      } else if (error) {
        showErrorModal({
          btnText: 'OK',
          content: <div>{parseHtml(DOMPurify.sanitize(error?.message ?? ''))}</div>,
          errorCode: error?.code,
        });
      }
    },
    [fetchPostalAddress, form]
  );

  const renderAdditionalQuestion = useMemo(
    () => AdditionalItem?.map((item: QuestionnaireItem) => <Questionnaire key={item.LinkId} questionItem={item} />),
    [AdditionalItem]
  );

  const renderDependenciesQuestion = useMemo(
    () =>
      questionItem?.DependencyQuestions?.filter((question) =>
        question?.EnableWhen?.Answer.some((answer) => answer === formValue?.[questionItem?.LinkId])
      ).map((questionItem: QuestionnaireItem) => (
        <Questionnaire key={questionItem.LinkId} questionItem={questionItem} />
      )),
    [formValue, questionItem?.LinkId, questionItem?.DependencyQuestions]
  );

  const renderGroupQuestionnaire = useCallback(() => {
    const handleRemoveQuestion = () => {
      dispatch(
        appointmentAction.setPreQuestionnaire({
          currentNumberOfAddButton: {
            ...currentNumberOfAddButton,
            [LinkId]: currentNumberOfAddButton[LinkId] - 1,
          },
        })
      );
      form.resetFields(GroupItem?.map((item) => `${item.LinkId}_${currentNumberOfAddButton[LinkId] - 1}`));
    };

    const handleAddQuestion = () => {
      dispatch(
        appointmentAction.setPreQuestionnaire({
          currentNumberOfAddButton: {
            ...currentNumberOfAddButton,
            [LinkId]: currentNumberOfAddButton[LinkId] + 1,
          },
        })
      );
    };

    const hasAdditionalQuestions = AdditionalItem && AdditionalItem.length > 0;
    return (
      <QuestionForm helperNote={HelperItem} question={AdditionalQuestionKey ? '' : Text} required={!!Required}>
        <div>
          <QuestionnaireGroup additionalQuestionKey={AdditionalQuestionKey} multipleQuestions={GroupItem ?? []} />
          {!!hasAdditionalQuestions && currentNumberOfAddButton[LinkId] > 1 && renderAdditionalQuestion}
          {!!Repeats && (
            <>
              {MaxNumberOfAddButton > 0 &&
                Array.from(
                  {
                    length: currentNumberOfAddButton[LinkId] - CurrentNumberOfAddButton,
                  },
                  (_, id) => (
                    <div className="mt-4" key={id}>
                      <QuestionnaireGroup
                        additionalQuestionKey={CurrentNumberOfAddButton + id}
                        multipleQuestions={GroupItem ?? []}
                      />
                    </div>
                  )
                )}
              <div className="mb-4 flex w-1/2 gap-4">
                {currentNumberOfAddButton[LinkId] > 1 && (
                  <Button
                    className="flex w-1/2 items-center justify-center rounded-lg hover:opacity-60"
                    onClick={handleRemoveQuestion}
                    variant="secondary"
                  >
                    {t('appointment.button.remove')}
                  </Button>
                )}
                {currentNumberOfAddButton[LinkId] < MaxNumberOfAddButton && (
                  <Button
                    className="flex w-1/2 cursor-pointer items-center justify-center rounded-lg border-theme-700 hover:bg-slate-200"
                    onClick={handleAddQuestion}
                    variant="secondary"
                  >
                    <OutlinedAddIcon className="mr-2 size-5 text-theme-700 [&_path]:stroke-primary-blue-grey" /> Add
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </QuestionForm>
    );
  }, [
    AdditionalItem,
    AdditionalQuestionKey,
    CurrentNumberOfAddButton,
    GroupItem,
    HelperItem,
    LinkId,
    MaxNumberOfAddButton,
    Repeats,
    Required,
    Text,
    currentNumberOfAddButton,
    dispatch,
    form,
    renderAdditionalQuestion,
    t,
  ]);

  const renderQuestionFormItem = useCallback(
    (additionalRules: Rule[]) => {
      const disabledDate = (current: any): any => current && current > TODAY.startOf('day').add(1, 'day');
      switch (questionType) {
        case QuestionType.Boolean:
          return (
            <Form.Item
              name={LinkId}
              rules={[requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required })]}
            >
              <CustomRadioOption
                className="gbomit"
                direction={IsHorizontal ? 'horizontal' : 'vertical'}
                options={YES_NO_QUESTIONS}
              />
            </Form.Item>
          );
        case QuestionType.Choice:
          return (
            <Form.Item
              name={LinkId}
              rules={[requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required })]}
            >
              <CustomRadioOption
                className="gbomit"
                options={
                  AnswerOption?.map((answer) => ({
                    isUseHtmlForLabel: checkHtmlContainsString(answer),
                    label: answer,
                    value: answer,
                  })) || []
                }
              />
            </Form.Item>
          );
        case QuestionType.Date:
          return (
            <Form.Item
              getValueProps={(value) => ({ value: value ? dayjsWithTimezone(value) : null })}
              name={LinkId}
              rules={[
                requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required }),
                validDateRule(),
                disabledDateRule(disabledDate),
              ]}
              valuePropName="value"
            >
              <CustomDatePicker disabledDate={disabledDate} placeholder="Select date" required={!!Required} />
            </Form.Item>
          );
        case QuestionType.Decimal:
        case QuestionType.Integer:
          return (
            <Form.Item
              className={twClassMerge({ 'international-phone-form-item': isPhoneNumberField })}
              name={LinkId}
              rules={[
                requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required }),
                ...additionalRules,
              ]}
            >
              {isPhoneNumberField ? (
                <CustomInternationalPhoneInput
                  allowInternational={false}
                  layoutView={{ isMobileView, isSharedServiceView }}
                  required={!!Required}
                />
              ) : (
                <InputNumber
                  className="gbmask w-full rounded-lg py-3 lg:w-1/2"
                  maxLength={10}
                  onBeforeInput={(e) => {
                    const inputEvent = e.nativeEvent as InputEvent;
                    if (!validateIsNumber(inputEvent.data ?? '')) {
                      e.preventDefault();
                    }
                  }}
                />
              )}
            </Form.Item>
          );
        case QuestionType.Dropdown:
          return (
            <Form.Item
              initialValue={AnswerOption?.[0]}
              name={LinkId}
              rules={[requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required })]}
            >
              <CustomSelect
                layoutView={{ isMobileView, isSharedServiceView }}
                options={
                  AnswerOption?.map((answer) => ({
                    label: answer,
                    value: answer,
                  })) || []
                }
                placeholder={AnswerOption?.length ? '' : Text}
                popupDrawerTitle={AnswerOption?.length ? '' : Text}
              />
            </Form.Item>
          );
        case QuestionType.PostalCode:
          return (
            <div className="flex w-full justify-between gap-2 lg:w-1/2">
              <Form.Item
                className="w-1/2"
                name={LinkId}
                rules={[
                  requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required }),
                  ...additionalRules,
                ]}
              >
                <InputNumber
                  autoComplete="postal-code"
                  className="gbmask flex h-14 w-full items-center rounded-lg"
                  onBeforeInput={(e) => {
                    const inputEvent = e.nativeEvent as InputEvent;
                    if (!validateIsNumber(inputEvent.data ?? '')) {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
              <Form.Item dependencies={[LinkId]} noStyle>
                {({ getFieldValue }) => (
                  <Button
                    className="h-14 w-1/2 rounded-lg hover:opacity-70"
                    disabled={!getFieldValue(LinkId) || !validatePostalCode(getFieldValue(LinkId))}
                    onClick={() => onGetMyAddress(form.getFieldValue(LinkId))}
                    variant="secondary"
                  >
                    Get My Address
                  </Button>
                )}
              </Form.Item>
            </div>
          );
        case QuestionType.String:
          return (
            <Form.Item
              name={LinkId}
              rules={[
                requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required }),
                ...additionalRules,
              ]}
            >
              <CustomInput
                autoComplete={getAutoComplete()}
                label={Text}
                maxLength={255}
                onBeforeInput={(e) => {
                  const inputEvent = e.nativeEvent as InputEvent;
                  if (isEmailField && validateWhiteSpace(inputEvent.data ?? ' ')) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>
          );
        default:
          return (
            <Form.Item
              name={LinkId}
              rules={[requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: !!Required })]}
            >
              <CheckboxGroupInput
                boldLabel={false}
                options={
                  AnswerOption?.map((answer) => ({
                    label: removeHtmlInString(answer),
                    value: checkHtmlContainsString(answer) ? removeHtmlInString(answer) : answer,
                  })) || []
                }
              />
            </Form.Item>
          );
      }
    },
    [
      AnswerOption,
      IsHorizontal,
      LinkId,
      Required,
      Text,
      form,
      isPhoneNumberField,
      isEmailField,
      onGetMyAddress,
      questionType,
      t,
      isMobileView,
      isSharedServiceView,
      getAutoComplete,
    ]
  );

  if (questionType === QuestionType.Display) {
    return !IsRootGroup && KKH_QUESTIONNAIRE_IDS.includes(questionnaireId ?? '') ? ( // check subject question
      <div className="flex items-center justify-start">
        <div>
          {parseHtml(
            DOMPurify.sanitize(Text, {
              ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
            })
          )}
        </div>
        {!!HelperItem && <QuestionCircleOutlined className="ml-2 cursor-pointer" onClick={onToggle} />}
        <HelperQuestionnaireModal
          content={HelperItem?.Text ?? ''}
          onCancel={onToggle}
          onOk={onToggle}
          open={isVisible}
        />
      </div>
    ) : (
      <div className="flex h-[calc(100vh-18rem)] items-center justify-center">
        <div className="flex flex-col text-center text-lg font-semibold [&_p]:font-normal">
          {parseHtml(
            DOMPurify.sanitize(Text, {
              ALLOWED_ATTR: ['href', 'target', 'rel', 'style'],
            })
          )}
        </div>
        {!!HelperItem && <QuestionCircleOutlined className="ml-2 cursor-pointer" onClick={onToggle} />}
        <HelperQuestionnaireModal
          content={HelperItem?.Text ?? ''}
          onCancel={onToggle}
          onOk={onToggle}
          open={isVisible}
        />
      </div>
    );
  }
  if (questionType === QuestionType.Group) {
    return renderGroupQuestionnaire();
  }

  if (isValidQuestionType) {
    if (questionType === QuestionType.Text) {
      return (
        <Form.Item
          name={LinkId}
          required={!!Required}
          rules={[
            ...(Required
              ? [requiredRule({ message: t('appointment.defaultMessageQuestionnaire'), required: true })]
              : []),
            ...(validationRules ?? []),
          ]}
        >
          <CustomTextArea label={Text} maxLength={255} required={!!Required} />
        </Form.Item>
      );
    }
    return (
      <QuestionForm helperNote={HelperItem} question={Text} required={!!Required}>
        <>
          {renderQuestionFormItem(validationRules ?? [])}
          {renderDependenciesQuestion}
        </>
      </QuestionForm>
    );
  }

  return null;
};

export default Questionnaire;
